import React, { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { cn } from '../lib/utils';

export type ImageGenerationStatus = 'idle' | 'generating' | 'complete';

export interface ImageGenerationPreviewProps {
  status: ImageGenerationStatus;
  imageUrl?: string;
  onGenerate?: () => void;
  className?: string;
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return reduced;
}

function GeneratingCanvas({ reducedMotion }: { reducedMotion: boolean }) {
  return (
    <div
      className="absolute inset-0 overflow-hidden bg-gradient-to-br from-muted via-background to-muted dark:from-zinc-900 dark:via-zinc-950 dark:to-zinc-900"
      aria-hidden
    >
      <div
        className={cn(
          'absolute -left-[10%] top-[8%] h-[55%] w-[55%] rounded-full bg-violet-400/30 blur-3xl dark:bg-violet-500/20',
          !reducedMotion && 'animate-gen-drift-a'
        )}
      />
      <div
        className={cn(
          'absolute -right-[8%] top-[20%] h-[48%] w-[48%] rounded-full bg-sky-400/25 blur-3xl dark:bg-sky-500/15',
          !reducedMotion && 'animate-gen-drift-b'
        )}
      />
      <div
        className={cn(
          'absolute bottom-[5%] left-[25%] h-[42%] w-[50%] rounded-full bg-amber-300/20 blur-3xl dark:bg-amber-400/10',
          !reducedMotion && 'animate-gen-drift-c'
        )}
      />
      {!reducedMotion && (
        <div className="absolute inset-0 overflow-hidden opacity-40 dark:opacity-25">
          <div className="absolute inset-y-0 w-1/2 bg-gradient-to-r from-transparent via-white/40 to-transparent dark:via-white/10 animate-gen-shimmer" />
        </div>
      )}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,hsl(var(--background)/0.35)_100%)]" />
    </div>
  );
}

export const ImageGenerationPreview: React.FC<ImageGenerationPreviewProps> = ({
  status,
  imageUrl,
  onGenerate,
  className,
}) => {
  const reducedMotion = usePrefersReducedMotion();
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (status !== 'complete') setImageLoaded(false);
  }, [status, imageUrl]);

  return (
    <div className={cn(className)}>
      <Card className="overflow-hidden border bg-card/50 p-2 shadow-sm">
        <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-muted/30">
          {status === 'idle' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
              <div className="rounded-full border border-dashed border-muted-foreground/25 p-5">
                <Sparkles className="h-6 w-6 text-muted-foreground/50" strokeWidth={1.5} />
              </div>
              <p className="text-sm text-muted-foreground">Your restyled image will appear here</p>
              {onGenerate && (
                <Button size="sm" variant="secondary" onClick={onGenerate}>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate
                </Button>
              )}
            </div>
          )}

          {status === 'generating' && (
            <>
              <GeneratingCanvas reducedMotion={reducedMotion} />
              <span className="sr-only">Generating image</span>
            </>
          )}

          {status === 'complete' && imageUrl && (
            <>
              {!imageLoaded && <GeneratingCanvas reducedMotion={reducedMotion} />}
              <img
                src={imageUrl}
                alt="Generated result"
                onLoad={() => setImageLoaded(true)}
                className={cn(
                  'absolute inset-0 h-full w-full object-cover',
                  imageLoaded && (reducedMotion ? 'opacity-100' : 'animate-gen-fade-in')
                )}
              />
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

export default ImageGenerationPreview;
